
import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "./contexts/AuthContext";

// Pages
import Index from "./pages/Index";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Courses from "./pages/Courses";
import Checkout from "./pages/Checkout";
import NewCheckout from "./pages/NewCheckout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import TestPaymentFlow from "./pages/TestPaymentFlow";
import Auth from "./pages/Auth";
import AuthConfirm from "./pages/AuthConfirm";
import EmailConfirmSuccess from "./pages/EmailConfirmSuccess";
import NotFound from "./pages/NotFound";
import CourseDetail from "./pages/CourseDetail";
import CourseDetailPage from "./pages/CourseDetailPage";
import Trainings from "./pages/Trainings";
import Offerings from "./pages/Offerings";
import Coaching from "./pages/Coaching";
import Hypnotherapy from "./pages/Hypnotherapy";
import SomaticCoaching from "./pages/SomaticCoaching"; 
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import UserProfile from "./pages/UserProfile";
import MyCourses from "./pages/MyCourses";
import LearnerCoursePage from "./pages/LearnerCoursePage";
import LessonView from "./pages/LessonView";
import SmokeAuth from "./pages/SmokeAuth";

// Components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import ScrollToTop from "./components/layout/ScrollToTop";
import "./App.css";

// Create a new QueryClient instance
const queryClient = new QueryClient();

function App() {
  const isStealth = import.meta.env.VITE_STEALTH_MODE === "true";

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light">
        <AuthProvider>
          <div className="flex flex-col min-h-screen">
            {isStealth && (
              <div className="bg-yellow-200 text-yellow-900 text-[10px] font-bold text-center py-0.5 uppercase tracking-wider sticky top-0 z-[100]">
                Uus platvorm (Stealth Mode)
              </div>
            )}
            <Header />
            <main className="flex-grow">
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/confirm" element={<AuthConfirm />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/course/slug/:slug" element={<CourseDetailPage />} />
                <Route path="/course/:courseId" element={<CourseDetail />} />
                <Route path="/learner/course/:courseId" element={<LearnerCoursePage />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/:courseId" element={<NewCheckout />} />
                <Route path="/checkout-success" element={<CheckoutSuccess />} />
                <Route path="/my-courses" element={<MyCourses />} />
                <Route path="/coaching" element={<Coaching />} />
                <Route path="/about" element={<About />} />
                <Route path="/somatic-coaching" element={<SomaticCoaching />} />
                <Route path="/hypnotherapy" element={<Hypnotherapy />} />
                <Route path="/trainings" element={<Trainings />} />
                <Route path="/test-payment-flow" element={<TestPaymentFlow />} />
                <Route path="/email-confirm-success" element={<EmailConfirmSuccess />} />
                {import.meta.env.DEV && <Route path="/smoke-auth" element={<SmokeAuth />} />}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
          </div>
          <Toaster />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
